import { Redirect, Route, Switch } from "wouter";
import { useLocation } from "wouter";
import { lazy, Suspense, type ReactNode, useEffect } from "react";
import { useTranslation } from "react-i18next";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import Home from "./pages/Home";

const About = lazy(() => import("./pages/About"));
const Experiences = lazy(() => import("./pages/Experiences"));
const Contact = lazy(() => import("./pages/Contact"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const VIPTransfers = lazy(() => import("./pages/VIPTransfers"));
const TourDetail = lazy(() => import("./pages/TourDetail"));
const NotFound = lazy(() => import("./pages/NotFound"));

function LocaleUrlSync() {
  const [location] = useLocation();
  const { i18n } = useTranslation();

  useEffect(() => {
    const locale = i18n.language === "tr" ? "tr" : "en";
    const current = new URL(window.location.href);
    if (current.searchParams.get("lang") === locale) return;

    current.searchParams.set("lang", locale);
    window.history.replaceState(
      window.history.state,
      "",
      `${current.pathname}${current.search}${current.hash}`
    );
  }, [location, i18n.language]);

  return null;
}

function Router() {
  // make sure to consider if you need authentication for certain routes
  return (
    <Suspense
      fallback={
        <main className="min-h-screen bg-background" aria-busy="true" />
      }
    >
      <Switch>
        <Route path="/" component={Home} />
        <Route path="/about" component={About} />
        <Route path="/experiences" component={Experiences} />
        <Route path="/tours">
          <Redirect to="/experiences" />
        </Route>
        <Route path="/health-tourism/:id">
          <Redirect to="/" />
        </Route>
        <Route path="/health-tourism">
          <Redirect to="/" />
        </Route>
        <Route path="/contact" component={Contact} />
        <Route path="/blog" component={Blog} />
        <Route path="/blog/:id" component={BlogPost} />
        <Route path="/vip-transfers" component={VIPTransfers} />
        <Route path="/tours/:slug" component={TourDetail} />
        <Route path="/404" component={NotFound} />
        {/* Final fallback route */}
        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="light">
        <LocaleUrlSync />
        {children}
      </ThemeProvider>
    </ErrorBoundary>
  );
}

function App() {
  return (
    <AppShell>
      <Router />
    </AppShell>
  );
}

export default App;
